// src/components/Auth/Register.js - Firebase version with OTP verification
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  Alert,
  Link
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { registerUserWithoutVerification } from '../../services/firebaseAuth';
import api from '../../services/api';

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    birthdate: '',
    isCreator: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);

  const handleChange = e => {
    const { name, value, checked, type } = e.target;
    setForm(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleShowPassword = () => setShowPassword(p => !p);
  const toggleShowConfirmPassword = () => setShowConfirmPassword(p => !p);

  const handleSendOTP = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate password match
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    try {
      await api.post('/api/auth/send-otp', { email: form.email });
      setOtpSent(true);
      toast.success('Verification code sent to your email!');
    } catch (err) {
      console.error('Error sending OTP:', err);
      setError('Failed to send verification code. Please try again.');
      toast.error('Failed to send verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await api.post('/api/auth/verify-otp', {
        email: form.email,
        code: otpCode
      });
      setOtpVerified(true);
      toast.success('Email verified! Creating your account...');
      
      // Now register with Firebase without sending verification email
      const user = await registerUserWithoutVerification(form.email, form.password);
      
      // Get referral code from localStorage (captured from URL)
      const referralCode = localStorage.getItem('rewardful_ref');
      
      // Get Rewardful visitor_id if available (from localStorage, API, or cookie)
      let rewardfulVisitorId = localStorage.getItem('rewardful_visitor_id');
      
      // If not in localStorage, try to get from Rewardful API or cookie
      if (!rewardfulVisitorId) {
        try {
          if (window.rewardful) {
            // Try to get visitor_id from Rewardful API
            if (window.rewardful.visitor && window.rewardful.visitor.id) {
              rewardfulVisitorId = window.rewardful.visitor.id;
            } else {
              // Fallback: try to get from cookie
              const cookies = document.cookie.split(';');
              for (let cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === '_rwid' || name === 'rewardful_visitor_id') {
                  rewardfulVisitorId = value;
                  break;
                }
              }
            }
          }
          if (rewardfulVisitorId) {
            console.log('[Rewardful] Captured visitor_id:', rewardfulVisitorId);
            // Store it for future use
            localStorage.setItem('rewardful_visitor_id', rewardfulVisitorId);
          }
        } catch (e) {
          console.warn('[Rewardful] Could not get visitor_id:', e);
        }
      } else {
        console.log('[Rewardful] Using stored visitor_id:', rewardfulVisitorId);
      }
      
      // Sync user profile with MongoDB
      try {
        await api.post('/api/firebase-auth/sync-user', {
          username: form.username,
          birthdate: form.birthdate,
          isCreator: form.isCreator,
          referral_code: referralCode,  // Pass referral code to backend
          rewardful_visitor_id: rewardfulVisitorId  // Pass visitor_id to backend
        });
        
        // Clear referral code after successful registration
        if (referralCode) {
          localStorage.removeItem('rewardful_ref');
          console.log('[Rewardful] Referral code sent to backend:', referralCode);
        }
      } catch (syncError) {
        console.error('Error syncing user with MongoDB:', syncError);
      }
      
      toast.success('Account created successfully! Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 2000);
      
    } catch (err) {
      console.error('Verification error:', err);
      
      let errorMessage = 'Verification failed. Please try again.';
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered. Please login instead.';
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 8, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            Create Account
          </Typography>

          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 2, px: 1 }}
          >
            Sign up to see OF creator reviews
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {!otpSent ? (
            <Box component="form" onSubmit={handleSendOTP}>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  fullWidth
                  autoFocus
                  disabled={otpVerified}
                />

                <TextField
                  label="Username"
                  name="username"
                  value={form.username}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  required
                  fullWidth
                  helperText="At least 6 characters"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={form.confirmPassword && form.password !== form.confirmPassword}
                  helperText={
                    form.confirmPassword && form.password !== form.confirmPassword
                      ? 'Passwords do not match'
                      : ''
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowConfirmPassword} edge="end">
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  label="Birthdate"
                  name="birthdate"
                  type="date"
                  value={form.birthdate}
                  onChange={handleChange}
                  required
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="isCreator"
                      checked={form.isCreator}
                      onChange={handleChange}
                    />
                  }
                  label="I am a content creator"
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || (form.password && form.password !== form.confirmPassword)}
                >
                  {loading ? 'Sending Code...' : 'Send Verification Code'}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleVerifyOTP}>
              <Stack spacing={2}>
                <Alert severity="info">
                  A verification code has been sent to {form.email}. Please check your inbox (and spam folder).
                </Alert>
                
                <TextField
                  label="Verification Code"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                  helperText="Enter the 6-digit code from your email"
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading || !otpCode}
                >
                  {loading ? 'Verifying...' : 'Verify & Create Account'}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => {
                    setOtpSent(false);
                    setOtpCode('');
                    setError('');
                  }}
                  disabled={loading}
                >
                  Change Email
                </Button>
              </Stack>
            </Box>
          )}

          <Typography variant="body2" align="center" sx={{ mt: 2 }}>
            Already have an account?{' '}
            <Link component={RouterLink} to="/login">
              Log in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
}
